import { Box, Button, Typography } from '@mui/material';
import { Colors, Loading, Modal, ModalError } from '@vabembarqueja/embarque-ja-shared-components';
import { useState } from 'react';

import { ActionButton } from 'src/app/components/ActionButton';
import FileUpload from 'src/app/components/FileUpload';

interface ModalAnexoAditivoProps {
  solicitarMigracao: (file?: File) => Promise<void>;
  refreshUserInfo: () => Promise<void>;
  disabled?: boolean;
}

export const ModalAnexoAditivo = ({
  solicitarMigracao,
  refreshUserInfo,
  disabled
}: ModalAnexoAditivoProps) => {
  const [erroAnexoMinuta, setErroAnexoMinuta] = useState('');
  const [minutaContratual, setMinutaContratual] = useState<File | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const solicitarAnexoMinuta = () => {
    setOpen(true);
  };

  const solicitarMigracaoPlano = async () => {
    if (!minutaContratual) {
      setErroAnexoMinuta('Arquivo inválido: Envie um arquivo (Docx) de até 5Mb.');
      return;
    }

    try {
      setLoading(true);
      await solicitarMigracao(minutaContratual);
      await refreshUserInfo();
      setOpen(false);
    } catch (error) {
      console.error('Erro ao solicitar migração:', error);
      setErroAnexoMinuta('Ocorreu um erro ao anexar o arquivo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="contained" onClick={solicitarAnexoMinuta} disabled={disabled || loading}>
        <Typography variant="body1" textTransform="none" lineHeight={1}>
          Inserir Aditivo/Novo Contrato
        </Typography>
      </Button>

      <Modal
        title="Anexar aditivo contratual"
        open={open}
        onClose={() => !loading && setOpen(false)}
        actions={
          <>
            <ActionButton
              variant="outlined"
              color="primary"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancelar
            </ActionButton>
            <ActionButton
              variant="contained"
              color="primary"
              onClick={solicitarMigracaoPlano}
              disabled={loading || !minutaContratual}
            >
              Anexar
            </ActionButton>
          </>
        }
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: { xs: '100%', sm: 480 },
            minHeight: { xs: 160, sm: 190 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 1
              }}
            >
              <Loading show bgColor={Colors.white} />
            </Box>
          )}

          <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Para que possamos dar continuidade à solicitação, solicitamos a gentileza de anexar o
            aditivo.
          </Typography>

          <Box mt={1}>
            <FileUpload
              maxSize={5 * 1024 * 1024}
              accept={{
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
              }}
              onChange={([minuta]) => {
                setErroAnexoMinuta('');
                setMinutaContratual(minuta ?? null);
              }}
              inputText="Envie um arquivo (Docx) de até 5Mb."
            />
          </Box>
        </Box>
      </Modal>

      <ModalError
        open={Boolean(erroAnexoMinuta)}
        onClose={() => setErroAnexoMinuta('')}
        setOpen={() => setErroAnexoMinuta('')}
        message={erroAnexoMinuta}
      />
    </>
  );
};
